import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, Plus, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const inputCls = "w-full px-2 py-1.5 border border-slate-300 rounded text-sm";

/** Edit is offered only while the order is still "booked": hidden once manifested with a 3PL or connected to the own network. */
export const canEditShipment = (s) => {
  if (!s) return false;
  if (!["booked", "pending_assignment", "3pl_pending"].includes(String(s.status || "").toLowerCase())) return false;
  const real = [s.awb, s.awb_number].filter((a) => a && !String(a).startsWith("3616") && !String(a).startsWith("OWN-"));
  return !(real.length || s.delhivery_lrnum || s.delhivery_manifest_job_id);
};

/** Edit a saved shipment: real number of boxes, weight + size of each box, goods, contacts. Staff and admin. */
export default function ShipmentEditModal({ shipmentId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [f, setF] = useState({});
  const [origin, setOrigin] = useState({});
  const [dest, setDest] = useState({});
  const [billAmount, setBillAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [qf, setQf] = useState({ n: "", l: "", b: "", h: "", w: "", total: "" });
  const [targetTotal, setTargetTotal] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/v2/shipment-edit/${shipmentId}`);
        setData(d);
        setBoxes(d.boxes.map((b) => ({ ...b })));
        setF({ material_description: d.material_description, consignment_value: d.consignment_value ?? "", special_instructions: d.special_instructions, eway_bill_number: d.eway_bill_number, invoice_number: d.invoice_number || "" });
        setOrigin(d.origin); setDest(d.destination);
        setBillAmount("");
      } catch (e) { toast.error(e.response?.data?.detail || "Could not load the shipment"); onClose(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const totals = useMemo(() => {
    let vol = 0, chg = 0, dead = 0;
    boxes.forEach((b) => {
      const v = (num(b.l) * num(b.b) * num(b.h)) / (num(b.divisor) || 5000);
      vol += v; dead += num(b.deadWt); chg += Math.max(v, num(b.deadWt));
    });
    return { vol: vol.toFixed(2), dead: dead.toFixed(2), chg: chg.toFixed(2) };
  }, [boxes]);

  const needEway = num(f.consignment_value) > 50000;
  const needInvoice = needEway || num(totals.chg) >= 20;
  const setBox = (i, k, v) => setBoxes((bs) => bs.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const applyQuickFill = () => {
    const n = parseInt(qf.n, 10);
    if (!(n >= 1 && n <= 200)) { toast.error("Enter how many boxes (1 to 200)"); return; }
    const each = num(qf.total) > 0 ? Math.round((num(qf.total) / n) * 100) / 100 : num(qf.w);
    if (each <= 0) { toast.error("Enter the weight of each box, or the total weight of all boxes"); return; }
    const made = Array.from({ length: n }, () => ({ l: num(qf.l), b: num(qf.b), h: num(qf.h), deadWt: each, divisor: boxes[0]?.divisor || 5000 }));
    if (num(qf.total) > 0) made[n - 1].deadWt = Math.round((num(qf.total) - each * (n - 1)) * 100) / 100; // last box takes the rounding so the total is exact
    setBoxes(made);
    toast.success(`${n} boxes created - you can still change any box below`);
  };
  const addBox = (copyLast) => setBoxes((bs) => [...bs, copyLast && bs.length ? { ...bs[bs.length - 1] } : { l: 0, b: 0, h: 0, deadWt: 0, divisor: 5000 }]);

  const save = async () => {
    if (boxes.some((b) => num(b.deadWt) <= 0)) { toast.error("Every box needs a weight above 0 kg"); return; }
    if (needInvoice && !String(f.invoice_number || "").trim()) { toast.error("Invoice number is mandatory for this shipment"); return; }
    if (needEway && !/^\d{12}$/.test(f.eway_bill_number || "")) { toast.error("A 12-digit e-way bill number is mandatory above ₹50,000"); return; }
    const payload = {
      boxes: boxes.map((b) => ({ l: num(b.l), b: num(b.b), h: num(b.h), deadWt: num(b.deadWt), divisor: num(b.divisor) || 5000 })),
      invoice_number: f.invoice_number, material_description: f.material_description, special_instructions: f.special_instructions, eway_bill_number: f.eway_bill_number || "",
      consignment_value: f.consignment_value === "" ? undefined : num(f.consignment_value),
      origin: { name: origin.name, phone: origin.phone, address: origin.address, city: origin.city, pincode: origin.pincode },
      destination: { name: dest.name, phone: dest.phone, address: dest.address, city: dest.city, pincode: dest.pincode },
      reason,
    };
    if (data.is_admin && billAmount !== "") payload.bill_amount = num(billAmount);
    setSaving(true);
    try {
      const { data: r } = await api.patch(`/v2/shipment-edit/${shipmentId}`, payload);
      toast.success(r.message);
      if (r.price_note) toast.warning(r.price_note, { duration: 9000 });
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[60] overflow-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl my-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="shipment-edit-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-slate-800">Edit shipment {data?.order_id}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        {!data ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : data.locked_reason ? (
          <div className="p-6"><div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded p-3">{data.locked_reason}</div></div>
        ) : (
          <div className="p-4 space-y-5">
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Boxes ({boxes.length})</h4>
                <div className="flex gap-2">
                  <button type="button" onClick={() => addBox(true)} className="text-xs px-2 py-1 border rounded flex items-center gap-1 hover:bg-slate-50"><Copy className="w-3 h-3" /> Add same box</button>
                  <button type="button" onClick={() => addBox(false)} className="text-xs px-2 py-1 border rounded flex items-center gap-1 hover:bg-slate-50" data-testid="edit-add-box"><Plus className="w-3 h-3" /> Add box</button>
                </div>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-3" data-testid="quick-multibox">
                <p className="text-xs font-bold text-indigo-800 mb-2">Multi box - all boxes the same size? Fill once</p>
                <div className="grid grid-cols-3 sm:grid-cols-[repeat(6,1fr)_auto] gap-2 items-end">
                  {[["n", "No. of boxes"], ["l", "Length cm"], ["b", "Breadth cm"], ["h", "Height cm"], ["w", "Weight each kg"], ["total", "OR total kg (all boxes)"]].map(([k, lbl]) => (
                    <label key={k} className="text-[11px] text-slate-600 font-semibold">{lbl}
                      <input type="number" min="0" step="any" className={inputCls} value={qf[k]} onChange={(e) => setQf({ ...qf, [k]: e.target.value })} data-testid={`qf-${k}`} /></label>
                  ))}
                  <button type="button" onClick={applyQuickFill} data-testid="qf-apply" className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded hover:bg-indigo-700 col-span-3 sm:col-span-1">Create boxes</button>
                </div>
              </div>
              <div className="space-y-2">
                <div className="hidden sm:grid grid-cols-[2rem_repeat(4,1fr)_4.5rem_2rem] gap-2 text-[11px] text-slate-500 font-semibold">
                  <span>#</span><span>Length cm</span><span>Breadth cm</span><span>Height cm</span><span>Weight kg</span><span>Divisor</span><span />
                </div>
                {boxes.map((b, i) => (
                  <div key={i} className="grid grid-cols-3 sm:grid-cols-[2rem_repeat(4,1fr)_4.5rem_2rem] gap-2 items-center" data-testid={`edit-box-${i}`}>
                    <span className="text-xs font-bold text-slate-500">{i + 1}</span>
                    {["l", "b", "h", "deadWt"].map((k) => (
                      <input key={k} type="number" min="0" step="any" value={b[k]} onChange={(e) => setBox(i, k, e.target.value)} className={inputCls}
                        placeholder={k === "deadWt" ? "kg" : "cm"} data-testid={`edit-box-${i}-${k}`} />
                    ))}
                    <input type="number" value={b.divisor} onChange={(e) => setBox(i, "divisor", e.target.value)} className={inputCls} />
                    <button type="button" disabled={boxes.length === 1} onClick={() => setBoxes((bs) => bs.filter((_, j) => j !== i))} className="text-red-500 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-end gap-2 text-xs" data-testid="scale-total">
                <label className="font-semibold text-slate-600">Boxes already added - set the TOTAL weight to (kg)
                  <input type="number" min="0" step="any" className={`${inputCls} w-40 block`} value={targetTotal} onChange={(e) => setTargetTotal(e.target.value)} placeholder="e.g. 350" data-testid="scale-total-input" /></label>
                <button type="button" data-testid="scale-total-apply" className="px-3 py-1.5 bg-slate-700 text-white font-semibold rounded hover:bg-slate-800"
                  onClick={() => {
                    const t = num(targetTotal); const cur = boxes.reduce((a, b) => a + num(b.deadWt), 0);
                    if (t <= 0) { toast.error("Enter the total weight in kg"); return; }
                    const f = cur > 0 ? t / cur : 0;
                    const out = boxes.map((b) => ({ ...b, deadWt: f ? Math.round(num(b.deadWt) * f * 100) / 100 : Math.round((t / boxes.length) * 100) / 100 }));
                    const diff = Math.round((t - out.reduce((a, b) => a + b.deadWt, 0)) * 100) / 100;
                    out[out.length - 1].deadWt = Math.round((out[out.length - 1].deadWt + diff) * 100) / 100;
                    setBoxes(out); toast.success(`Box weights adjusted so the total is ${t} kg`);
                  }}>Adjust boxes to this total</button>
              </div>
              <div className="mt-2 text-xs bg-slate-50 border rounded p-2 flex flex-wrap gap-x-5 gap-y-1">
                <span>Dead weight: <b>{totals.dead} kg</b></span><span>Volumetric: <b>{totals.vol} kg</b></span>
                <span>Chargeable: <b className="text-brand-600">{totals.chg} kg</b></span>
                <span className="text-slate-500">Was {data.chargeable_weight} kg / {data.box_count} box(es)</span>
              </div>
            </section>

            <section className="grid sm:grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-slate-600">Goods description
                <input className={inputCls} value={f.material_description} onChange={(e) => setF({ ...f, material_description: e.target.value })} /></label>
              <label className="text-xs font-semibold text-slate-600">Goods value (₹)
                <input type="number" className={inputCls} value={f.consignment_value} onChange={(e) => setF({ ...f, consignment_value: e.target.value })} /></label>
              <label className="text-xs font-semibold text-slate-600">Invoice number {needInvoice && <span className="text-red-500">*</span>}
                <input className={`${inputCls} ${needInvoice && !String(f.invoice_number || "").trim() ? "border-red-300" : ""}`} value={f.invoice_number} maxLength={40} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} data-testid="edit-invoice-number" /></label>
              <label className="text-xs font-semibold text-slate-600">E-way bill number (12 digits) {needEway && <span className="text-red-500">*</span>}
                <input className={`${inputCls} ${needEway && !/^\d{12}$/.test(f.eway_bill_number || "") ? "border-red-300" : ""}`} value={f.eway_bill_number} maxLength={12} onChange={(e) => setF({ ...f, eway_bill_number: e.target.value.replace(/\D/g, "") })} /></label>
              <label className="text-xs font-semibold text-slate-600">Special instructions
                <input className={inputCls} value={f.special_instructions} onChange={(e) => setF({ ...f, special_instructions: e.target.value })} /></label>
            </section>

            <section className="grid sm:grid-cols-2 gap-4">
              {[["Pickup (sender)", origin, setOrigin], ["Delivery (receiver)", dest, setDest]].map(([title, v, set]) => (
                <div key={title} className="border rounded-lg p-3 space-y-2">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">{title} </h4>
                  {title.startsWith("Pickup") && data.pickup_location && (
                    <button type="button" data-testid="use-pickup-location"
                      onClick={() => set({ ...v, address: data.pickup_location.address, city: data.pickup_location.city || v.city, pincode: data.pickup_location.pincode || v.pincode })}
                      className="text-xs font-semibold text-brand-600 hover:underline text-left">
                      Use pickup location address ({data.pickup_location.name}, {data.pickup_location.city} {data.pickup_location.pincode})
                    </button>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <input className={inputCls} placeholder="City" value={v.city || ""} onChange={(e) => set({ ...v, city: e.target.value })} />
                    <input className={inputCls} placeholder="Pincode" maxLength={6} value={v.pincode || ""} onChange={(e) => set({ ...v, pincode: e.target.value.replace(/\D/g, "") })} data-testid={`edit-${title.startsWith("Pickup") ? "origin" : "dest"}-pincode`} />
                  </div>
                  <input className={inputCls} placeholder="Name" value={v.name} onChange={(e) => set({ ...v, name: e.target.value })} />
                  <input className={inputCls} placeholder="Phone" value={v.phone} onChange={(e) => set({ ...v, phone: e.target.value })} />
                  <textarea className={inputCls} rows={2} placeholder="Address" value={v.address} onChange={(e) => set({ ...v, address: e.target.value })} />
                </div>
              ))}
            </section>

            <section className="grid sm:grid-cols-2 gap-3 items-end">
              <div className="text-xs text-slate-600">Current bill amount: <b>₹{data.price ?? "—"}</b><br />
                <span className="text-slate-500">Changing weight or pincode does not change the bill automatically.</span></div>
              {data.is_admin && (
                <label className="text-xs font-semibold text-slate-600">New bill amount (₹) - admin only, optional
                  <input type="number" className={inputCls} value={billAmount} onChange={(e) => setBillAmount(e.target.value)} /></label>
              )}
              <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Reason for the change
                <input className={inputCls} value={reason} placeholder="e.g. customer sent 3 boxes, not 1" onChange={(e) => setReason(e.target.value)} /></label>
            </section>

            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              Saving clears any earlier manifest review - the shipment must be reviewed again before it can be sent to Delhivery.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 font-semibold">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="edit-save"
                className="px-5 py-2 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-600 disabled:opacity-50">{saving ? "Saving..." : "Save changes"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
